import React from 'react';
import { ArrowDown, RotateCcw, FileText, FileSpreadsheet, FileDown, Play, XCircle } from 'lucide-react';

/**
 * Report toolbar — Run/Clear | Sorting | Stats | Export
 */
const ReportToolbar = ({
  onRunReport, loading, activeFilterCount, onClearFilters,
  isDefaultSort, sortOrder, defaultSort, onResetSort,
  totalCount, executionMs,
  pageSize, pageSizeLabel, totalPages,
  hasRun,
  onExportPdf = null, onExportExcel = null, onExportCsv = null,
  exportProgress = null
}) => {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>

      {/* 1. Run Report + Clear Filters */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <button
          className="sync-acknowledge-btn"
          onClick={onRunReport}
          disabled={loading}
          style={loading ? { cursor: 'wait', opacity: 0.7 } : {}}
        >
          <Play size={16} /> {loading ? 'Running...' : 'Run Report'}
        </button>
        {activeFilterCount > 0 && (
          <button
            onClick={onClearFilters}
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              padding: '7px 14px', background: '#fff', color: '#dc2626',
              border: '1px solid #dc2626', borderRadius: '4px', cursor: 'pointer',
              fontWeight: 600, fontSize: '12px', height: '34px'
            }}
          >
            <XCircle size={13} /> Clear Filters
          </button>
        )}
      </div>

      <Divider />

      {/* 2. Sorting */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {sortOrder.length > 0 ? (
          <>
            <span style={{ fontSize: '11px', color: 'var(--color-gray-500)', fontWeight: 600 }}>Sort:</span>
            {sortOrder.map((s, i) => (
              <span key={s.field} style={{
                display: 'inline-flex', alignItems: 'center', gap: '3px',
                padding: '1px 6px', borderRadius: '8px', fontSize: '11px',
                background: 'var(--color-primary-light)', color: 'var(--color-primary)'
              }}>
                <span style={{
                  fontSize: '9px', fontWeight: 700,
                  background: 'var(--color-primary)', color: '#fff',
                  borderRadius: '50%', width: '14px', height: '14px',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {i + 1}
                </span>
                {s.field} {s.direction === 'asc' ? '\u2191' : '\u2193'}
              </span>
            ))}
          </>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--color-gray-400)', fontStyle: 'italic' }}>No sort</span>
        )}
        <button
          onClick={isDefaultSort ? undefined : onResetSort}
          title={isDefaultSort
            ? `Default sort: ${defaultSort.map(s => `${s.field} ${s.direction}`).join(', ')}`
            : 'Click to reset to default sort'
          }
          style={{
            display: 'flex', alignItems: 'center', gap: '3px',
            padding: '2px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
            border: `1px solid ${isDefaultSort ? 'var(--color-primary-light)' : '#ffc107'}`,
            background: isDefaultSort ? 'var(--color-primary-light)' : '#fff8e1',
            color: isDefaultSort ? 'var(--color-primary)' : '#856404',
            cursor: isDefaultSort ? 'default' : 'pointer'
          }}
        >
          {isDefaultSort ? (
            <><ArrowDown size={10} /> Default</>
          ) : (
            <><RotateCcw size={10} /> Reset</>
          )}
        </button>
      </div>

      <Divider />

      {/* 3. Stats: rows, pages, rows/page, layout, runtime */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {hasRun ? (
          <>
            <Stat label="Rows" value={totalCount.toLocaleString()} />
            <Stat label="Pages" value={totalPages} />
            <Stat label="Rows/Page" value={pageSize} />
            <Stat label="Layout" value={pageSizeLabel} />
            {executionMs != null && <Stat label="Time" value={`${executionMs}ms`} />}
          </>
        ) : (
          <span style={{ fontSize: '11px', color: 'var(--color-gray-400)', fontStyle: 'italic' }}>
            Run report to see results
          </span>
        )}
      </div>

      <Divider />

      {/* 4. Export buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '11px', color: 'var(--color-gray-500)', fontWeight: 600 }}>Export:</span>
        <ExportBtn icon={FileText} label={exportProgress ? 'Generating...' : 'PDF'} onClick={exportProgress ? null : onExportPdf} />
        <ExportBtn icon={FileSpreadsheet} label={exportProgress ? 'Generating...' : 'Excel'} onClick={exportProgress ? null : onExportExcel} />
        <ExportBtn icon={FileDown} label={exportProgress ? 'Generating...' : 'CSV'} onClick={exportProgress ? null : onExportCsv} />
      </div>
    </div>
  );
};

// Vertical divider
const Divider = () => (
  <div style={{ width: '1px', height: '24px', background: 'var(--color-gray-300)' }} />
);

// Stat label/value pair
const Stat = ({ label, value }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '9px', color: 'var(--color-gray-400)', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>
      {label}
    </div>
    <div style={{ fontSize: '12px', color: 'var(--color-gray-700)', fontWeight: 600 }}>
      {value}
    </div>
  </div>
);

// Export button
const ExportBtn = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    disabled={!onClick}
    title={`Export ${label}`}
    style={{
      display: 'flex', alignItems: 'center', gap: '3px',
      padding: '4px 8px', borderRadius: '4px', fontSize: '11px',
      border: '1px solid var(--color-gray-300)',
      background: onClick ? '#fff' : 'var(--color-gray-100)',
      color: onClick ? 'var(--color-gray-700)' : 'var(--color-gray-400)',
      cursor: onClick ? 'pointer' : 'not-allowed'
    }}
  >
    <Icon size={12} /> {label}
  </button>
);

export default ReportToolbar;
